import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The shop cart. Client-only: the server has no cart, it has `POST /orders`,
 * so this is just the lines a member has gathered before they check out.
 *
 * Persisted because a phone app is interrupted constantly — a call, a
 * notification, the OS reclaiming the WebView — and losing a basket to that is
 * the fastest way to lose the sale. Prices are NOT persisted as truth: they are
 * kept only to render the basket, and checkout re-prices server-side from the
 * product ids, so a stale cached price can never become a stale charge.
 */
export type CartLine = {
  productId: string;
  variantId: string | null;
  name: string;
  variantName: string | null;
  /** Display only. The server re-prices at checkout. */
  unitPrice: string;
  qty: number;
};

type CartState = {
  lines: CartLine[];
  add: (line: Omit<CartLine, 'qty'>, qty?: number) => void;
  setQty: (productId: string, variantId: string | null, qty: number) => void;
  remove: (productId: string, variantId: string | null) => void;
  clear: () => void;
};

const same = (a: CartLine, productId: string, variantId: string | null) =>
  a.productId === productId && (a.variantId ?? null) === (variantId ?? null);

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      add: (line, qty = 1) =>
        set((s) => {
          const at = s.lines.findIndex((l) => same(l, line.productId, line.variantId));
          const existing = at === -1 ? undefined : s.lines[at];
          if (!existing) return { lines: [...s.lines, { ...line, qty }] };
          const lines = s.lines.slice();
          lines[at] = { ...existing, qty: existing.qty + qty };
          return { lines };
        }),
      setQty: (productId, variantId, qty) =>
        set((s) => ({
          lines:
            qty <= 0
              ? s.lines.filter((l) => !same(l, productId, variantId))
              : s.lines.map((l) => (same(l, productId, variantId) ? { ...l, qty } : l)),
        })),
      remove: (productId, variantId) =>
        set((s) => ({ lines: s.lines.filter((l) => !same(l, productId, variantId)) })),
      clear: () => set({ lines: [] }),
    }),
    { name: 'mp-cart' },
  ),
);

export const cartCount = (lines: CartLine[]): number => lines.reduce((n, l) => n + l.qty, 0);
